// src/api/QuestController.js
import { API_DELETE, API_GET, API_POST, API_PUT } from "../config/axiosConfig";

/**
 * Fetch quest state for a given room + user.
 * Backend route: GET /web/quests/state?roomId=X&userId=Y
 */
export async function getQuestState(roomId, userId) {
    console.log(`[QuestController] getQuestState called - roomId: ${roomId}, userId: ${userId}`);
    try {
        if (!roomId || !userId) {
            console.error("[QuestController] getQuestState: roomId and userId are required", { roomId, userId });
            return null;
        }

        const url = `${process.env.REACT_APP_CORE_API_URI}/web/quests/state?roomId=${encodeURIComponent(
            roomId
        )}&userId=${encodeURIComponent(userId)}`;

        const response = await API_GET(url, "", "GET");

        if (response && response.status === 200) {
            console.log(`[QuestController] getQuestState success:`, response.data);
            // For debugging if needed:
            // console.log("getQuestState response:", response.data);
            return response;
        }

        console.warn("[QuestController] getQuestState: non-200 response", response?.status, response?.data);
        return null;
    } catch (error) {
        console.error("[QuestController] Error fetching quest state:", error);
        return null;
    }
}

/**
 * Advance quest progress for a given room + user.
 * eventType = null for button advance
 * eventType = "OPEN_GUESTBOOK" / "EDIT_RPM_AVATAR" etc for event-based steps
 *
 * Expects backend route: POST /web/quests/progress/advance
 */
export async function advanceQuestProgress({ roomId, userId, eventType }) {
    console.log(
        `[QuestController] advanceQuestProgress called - roomId: ${roomId}, userId: ${userId}, eventType: ${eventType}`
    );
    try {
        if (!roomId || !userId) {
            console.error("[QuestController] advanceQuestProgress: roomId and userId are required", {
                roomId,
                userId,
                eventType,
            });
            return null;
        }

        const url = `${process.env.REACT_APP_CORE_API_URI}/web/quests/progress/advance`;

        const data = {
            roomId,
            userId,
            eventType: eventType ?? null,
        };

        const response = await API_POST(url, data, "", "POST");

        if (response && response.status === 200) {
            console.log(`[QuestController] advanceQuestProgress success:`, response.data);
            // For debugging if needed:
            // console.log("advanceQuestProgress response:", response.data);
            return response;
        }

        console.warn("[QuestController] advanceQuestProgress: non-200 response", response?.status, response?.data);
        return null;
    } catch (error) {
        console.error("[QuestController] Error advancing quest progress:", error);
        return null;
    }
}
/**
 * Fetch a quest with its steps.
 * Backend route: GET admin/web/quest-admin/quests/:questId/with-steps
 * (token, questId) => { status, data: { quest, steps } }
 */
export async function getQuestWithSteps(token, questId) {
    console.log(`[QuestController] getQuestWithSteps called - questId: ${questId}`);
    try {
        if (!token || !questId) {
            console.error("[QuestController] getQuestWithSteps: token and questId are required", {
                token: !!token,
                questId,
            });
            return null;
        }

        const url = `${process.env.REACT_APP_CORE_API_URI}/web/quest-admin/quests/${encodeURIComponent(questId)}/with-steps`;

        const response = await API_GET(url);

        if (response && response.status === 200) {
            console.log(`[QuestController] getQuestWithSteps success:`, response.data);
            return response;
        }

        console.warn("[QuestController] getQuestWithSteps: non-200 response", response?.status, response?.data);
        return null;
    } catch (error) {
        console.error("[QuestController] Error fetching quest with steps:", error);
        return null;
    }
}

/**
 * Create a new quest.
 * Backend route: POST /web/quest-admin/quests
 * Body: { name, description, starting_room_id, isActive }
 * (token, payload) => { status, data: { questId } }
 */
export async function createQuest(token, payload) {
    console.log(`[QuestController] createQuest called with payload:`, payload);
    try {
        if (!token) {
            console.error("[QuestController] createQuest: token is required");
            return null;
        }

        if (!payload?.name || !payload?.starting_room_id) {
            console.error("[QuestController] createQuest: name and starting_room_id are required", payload);
            return null;
        }

        const url = `${process.env.REACT_APP_CORE_API_URI}/web/quest-admin/quests`;

        const data = {
            name: payload.name,
            description: payload.description ?? "",
            starting_room_id: payload.starting_room_id,
            isActive: payload.isActive ?? true,
        };

        const response = await API_POST(url, data);

        if (response && (response.status === 201 || response.status === 200)) {
            console.log(`[QuestController] createQuest success:`, response.data);
            return response;
        }

        console.warn("[QuestController] createQuest: non-201/200 response", response?.status, response?.data);
        return null;
    } catch (error) {
        console.error("[QuestController] Error creating quest:", error);
        return null;
    }
}

/**
 * Update an existing quest.
 * Backend route: PUT web/quest-admin/quests/:questId
 * Body: { questId?, name, description, starting_room_id, isActive }
 * (token, payload) => { status }
 */
export async function updateQuest(token, payload) {
    console.log(`[QuestController] updateQuest called with payload:`, payload);
    try {
        if (!token) {
            console.error("[QuestController] updateQuest: token is required");
            return null;
        }

        const effectiveQuestId = payload?.questId;
        if (!effectiveQuestId) {
            console.error("[QuestController] updateQuest: questId is required", payload);
            return null;
        }

        const url = `${process.env.REACT_APP_CORE_API_URI}/web/quest-admin/quests/${encodeURIComponent(effectiveQuestId)}`;

        const data = {
            questId: effectiveQuestId,
            ...(payload.name !== undefined ? { name: payload.name } : {}),
            ...(payload.description !== undefined ? { description: payload.description } : {}),
            ...(payload.starting_room_id !== undefined ? { starting_room_id: payload.starting_room_id } : {}),
            ...(payload.isActive !== undefined ? { isActive: payload.isActive } : {}),
        };

        const response = await API_PUT(url, data);

        if (response && (response.status === 200 || response.status === 204)) {
            console.log(`[QuestController] updateQuest success for questId: ${effectiveQuestId}`);
            return response;
        }

        console.warn("[QuestController] updateQuest: non-200/204 response", response?.status, response?.data);
        return null;
    } catch (error) {
        console.error("[QuestController] Error updating quest:", error);
        return null;
    }
}

/**
 * Toggle quest active state.
 * Backend route: PUT /web/quest-admin/quests/:questId/active
 * Body: { isActive }
 * (token, questId, isActive) => { status }
 */
export async function toggleQuestActive(token, questId, isActive) {
    console.log(`[QuestController] toggleQuestActive called - questId: ${questId}, isActive: ${isActive}`);
    try {
        if (!token || !questId) {
            console.error("[QuestController] toggleQuestActive: token and questId are required", {
                token: !!token,
                questId,
                isActive,
            });
            return null;
        }

        if (isActive === undefined) {
            console.error("[QuestController] toggleQuestActive: isActive is required", { questId, isActive });
            return null;
        }

        const url = `${process.env.REACT_APP_CORE_API_URI}/web/quest-admin/quests/${encodeURIComponent(questId)}/active`;

        const data = { isActive: !!isActive };

        const response = await API_PUT(url, data);

        if (response && (response.status === 200 || response.status === 204)) {
            console.log(`[QuestController] toggleQuestActive success for questId: ${questId}`);
            return response;
        }

        console.warn("[QuestController] toggleQuestActive: non-200/204 response", response?.status, response?.data);
        // Return the response so caller can check for specific error codes
        return response;
    } catch (error) {
        console.error("[QuestController] Error toggling quest active:", error);
        // Return the error response so caller can check for specific error codes (e.g., 409 conflict)
        if (error?.response) {
            return error.response;
        }
        return { status: 500, data: { message: error?.message || "Unknown error" } };
    }
}

/**
 * Create a new quest step.
 * Backend route: POST admin/web/quest-admin/quests/:questId/steps
 * Body (snake_case): { room_id, step_index, prompt_text, button_label, required_event, metadata }
 * (token, payload) => { status, data: { questStepId } }
 */
export async function createQuestStep(token, payload) {
    console.log(`[QuestController] createQuestStep called with payload:`, payload);
    try {
        if (!token) {
            console.error("[QuestController] createQuestStep: token is required");
            return null;
        }

        const effectiveQuestId = payload?.questId;
        if (!effectiveQuestId) {
            console.error("[QuestController] createQuestStep: payload.questId is required", payload);
            return null;
        }

        // required by backend
        const requiredMissing =
            payload?.step_index === undefined ||
            payload?.prompt_text === undefined ||
            payload?.button_label === undefined ||
            payload?.required_event === undefined;

        if (requiredMissing) {
            console.error(
                "[QuestController] createQuestStep: step_index, prompt_text, button_label, required_event are required",
                payload
            );
            return null;
        }

        const url = `${process.env.REACT_APP_CORE_API_URI}/web/quest-admin/quests/${encodeURIComponent(effectiveQuestId)}/steps`;

        const data = {
            questId: effectiveQuestId, // backend supports path param + body fallback
            room_id: payload.room_id ?? null,
            step_index: payload.step_index,
            prompt_text: payload.prompt_text,
            button_label: payload.button_label,
            required_event: payload.required_event,
            metadata: payload.metadata ?? {},
        };

        const response = await API_POST(url, data);

        if (response && (response.status === 201 || response.status === 200)) {
            console.log(`[QuestController] createQuestStep success:`, response.data);
            return response;
        }

        console.warn("[QuestController] createQuestStep: non-201/200 response", response?.status, response?.data);
        return null;
    } catch (error) {
        console.error("[QuestController] Error creating quest step:", error);
        return null;
    }
}

/**
 * Update an existing quest step.
 * Backend route: PUT web/quest-admin/quest-steps/:questStepId
 * Body (snake_case): { questStepId?, room_id, step_index, prompt_text, button_label, required_event, metadata }
 * (token, payload) => { status }
 */
export async function updateQuestStep(token, payload) {
    console.log(`[QuestController] updateQuestStep called with payload:`, payload);
    try {
        if (!token) {
            console.error("[QuestController] updateQuestStep: token is required");
            return null;
        }

        const effectiveQuestStepId = payload?.questStepId;
        if (!effectiveQuestStepId) {
            console.error("[QuestController] updateQuestStep: questStepId is required", payload);
            return null;
        }

        const url = `${process.env.REACT_APP_CORE_API_URI}/web/quest-admin/quest-steps/${encodeURIComponent(effectiveQuestStepId)}`;

        // Only include provided fields (keeps your API clean and matches your backend update pattern)
        const data = {
            questStepId: effectiveQuestStepId,
            ...(payload.room_id !== undefined ? { room_id: payload.room_id } : {}),
            ...(payload.step_index !== undefined ? { step_index: payload.step_index } : {}),
            ...(payload.prompt_text !== undefined ? { prompt_text: payload.prompt_text } : {}),
            ...(payload.button_label !== undefined ? { button_label: payload.button_label } : {}),
            ...(payload.required_event !== undefined ? { required_event: payload.required_event } : {}),
            ...(payload.metadata !== undefined ? { metadata: payload.metadata } : {}),
        };

        const response = await API_PUT(url, data);

        if (response && (response.status === 200 || response.status === 204)) {
            console.log(`[QuestController] updateQuestStep success for stepId: ${effectiveQuestStepId}`);
            return response;
        }

        console.warn("[QuestController] updateQuestStep: non-200/204 response", response?.status, response?.data);
        return null;
    } catch (error) {
        console.error("[QuestController] Error updating quest step:", error);
        return null;
    }
}

/**
 * Delete a quest step.
 * Backend route: DELETE web/quest-admin/quest-steps/:questStepId
 * (token, questStepId) => { status }
 */
export async function deleteQuestStep(token, questStepId) {
    console.log(`[QuestController] deleteQuestStep called - questStepId: ${questStepId}`);
    try {
        if (!token || !questStepId) {
            console.error("[QuestController] deleteQuestStep: token and questStepId are required", {
                token: !!token,
                questStepId,
            });
            return null;
        }

        const url = `${process.env.REACT_APP_CORE_API_URI}/web/quest-admin/quest-steps/${encodeURIComponent(questStepId)}`;

        const response = await API_DELETE(url);

        if (response && (response.status === 204 || response.status === 200)) {
            console.log(`[QuestController] deleteQuestStep success for stepId: ${questStepId}`);
            return response;
        }

        console.warn("[QuestController] deleteQuestStep: non-204/200 response", response?.status, response?.data);
        return null;
    } catch (error) {
        console.error("[QuestController] Error deleting quest step:", error);
        return null;
    }
}

/**
 * Reorder quest steps.
 * Backend route: PUT /web/quest-admin/quests/:questId/steps/reorder
 * Body: { steps: [{ questStepId, step_order }] }
 *
 * To avoid unique constraint issues, we use a two-phase approach:
 * Phase 1: Set all step_order values to temporary high offsets (original + 1000000)
 * Phase 2: Set the actual new step_order values
 *
 * (token, questId, orderedIdsOrSteps) => { status }
 */
export async function reorderQuestSteps(token, questId, orderedIdsOrSteps) {
    console.log(`[QuestController] reorderQuestSteps called - questId: ${questId}, steps:`, orderedIdsOrSteps);
    try {
        if (!token || !questId) {
            console.error("[QuestController] reorderQuestSteps: token and questId are required", {
                token: !!token,
                questId,
            });
            return null;
        }

        if (!Array.isArray(orderedIdsOrSteps) || orderedIdsOrSteps.length === 0) {
            console.error(
                "[QuestController] reorderQuestSteps: orderedIdsOrSteps must be a non-empty array",
                orderedIdsOrSteps
            );
            return null;
        }

        // Accept either:
        // - [{ questStepId, step_order }]
        // - [{ questStepId, stepIndex }] (we will map to step_order)
        // - [questStepId1, questStepId2, ...] (we will generate step_order)
        let finalSteps = [];

        if (typeof orderedIdsOrSteps[0] === "string") {
            finalSteps = orderedIdsOrSteps.map((id, idx) => ({
                questStepId: id,
                step_order: idx + 1,
            }));
        } else {
            finalSteps = orderedIdsOrSteps.map((s, idx) => ({
                questStepId: s.questStepId,
                step_order:
                    // eslint-disable-next-line no-nested-ternary
                    s.step_order !== undefined ? s.step_order : s.stepIndex !== undefined ? s.stepIndex : idx + 1,
            }));
        }

        const url = `${process.env.REACT_APP_CORE_API_URI}/web/quest-admin/quests/${encodeURIComponent(questId)}/steps/reorder`;

        // Phase 1: Set temporary high offset values to avoid unique constraint conflicts
        const OFFSET = 1000000;
        const tempSteps = finalSteps.map((s, idx) => ({
            questStepId: s.questStepId,
            step_order: OFFSET + idx + 1,
        }));

        console.log(`[QuestController] Phase 1: Setting temporary step_order values with offset ${OFFSET}...`);
        const tempData = { steps: tempSteps };
        const tempResponse = await API_PUT(url, tempData);

        if (!(tempResponse && (tempResponse.status === 200 || tempResponse.status === 204))) {
            console.warn(
                "[QuestController] reorderQuestSteps Phase 1 failed:",
                tempResponse?.status,
                tempResponse?.data
            );
            // Try direct approach as fallback (backend might handle it)
            console.log(`[QuestController] Falling back to direct reorder...`);
            const directData = { steps: finalSteps };
            const directResponse = await API_PUT(url, directData);

            if (directResponse && (directResponse.status === 200 || directResponse.status === 204)) {
                console.log(`[QuestController] reorderQuestSteps success (direct approach)`);
                return directResponse;
            }
            console.warn("[QuestController] reorderQuestSteps: direct approach also failed");
            return null;
        }

        // Phase 2: Set final step_order values
        console.log(`[QuestController] Phase 2: Setting final step_order values...`);
        const finalData = { steps: finalSteps };
        const response = await API_PUT(url, finalData);

        if (response && (response.status === 200 || response.status === 204)) {
            console.log(`[QuestController] reorderQuestSteps success for questId: ${questId}`);
            return response;
        }

        console.warn("[QuestController] reorderQuestSteps: Phase 2 failed", response?.status, response?.data);
        return null;
    } catch (error) {
        console.error("[QuestController] Error reordering quest steps:", error);
        return null;
    }
}

export async function getAllQuestsAdmin(token) {
    try {
        if (!token) {
            console.error("getAllQuestsAdmin: token is required");
            return null;
        }

        // You need a backend route for this:
        // GET admin/web/quest-admin/quests  -> { quests: [...] }
        const url = `${process.env.REACT_APP_CORE_API_URI}/web/quest-admin/quests`;

        const response = await API_GET(url);
        if (response && response.status === 200) return response;

        console.warn("getAllQuestsAdmin: non-200 response", response?.status, response?.data);
        return null;
    } catch (error) {
        console.error("Error fetching all quests (admin):", error);
        return null;
    }
}

export async function deleteQuestAdmin(token, questId) {
    try {
        if (!token || !questId) {
            console.error("deleteQuestAdmin: token and questId are required", { token: !!token, questId });
            return null;
        }

        // You need a backend route for this:
        // DELETE admin/web/quest-admin/quests/:questId
        const url = `${process.env.REACT_APP_CORE_API_URI}/web/quest-admin/quests/${encodeURIComponent(questId)}`;

        const response = await API_DELETE(url);
        if (response && (response.status === 204 || response.status === 200)) return response;

        console.warn("deleteQuestAdmin: non-204/200 response", response?.status, response?.data);
        return null;
    } catch (error) {
        console.error("Error deleting quest (admin):", error);
        return null;
    }
}

/**
 * Get active quest by room ID.
 * Backend route: GET /web/quest-admin/quests/active-by-room/:roomId
 * Returns: { questId, name } if an active quest exists, or null/empty if not
 * (token, roomId) => { status, data: { questId, name } | null }
 */
export async function getActiveQuestByRoom(token, roomId) {
    console.log(`[QuestController] getActiveQuestByRoom called - roomId: ${roomId}`);
    try {
        if (!token || !roomId) {
            console.error("[QuestController] getActiveQuestByRoom: token and roomId are required", {
                token: !!token,
                roomId,
            });
            return null;
        }

        const url = `${process.env.REACT_APP_CORE_API_URI}/web/quest-admin/quests/active-by-room/${encodeURIComponent(roomId)}`;

        const response = await API_GET(url);

        if (response && response.status === 200) {
            console.log(`[QuestController] getActiveQuestByRoom success:`, response.data);
            return response;
        }

        // 404 means no active quest in this room - not an error
        if (response && response.status === 404) {
            console.log(`[QuestController] getActiveQuestByRoom: no active quest in room ${roomId}`);
            return null;
        }

        console.warn("[QuestController] getActiveQuestByRoom: non-200 response", response?.status, response?.data);
        return null;
    } catch (error) {
        console.error("[QuestController] Error fetching active quest by room:", error);
        return null;
    }
}
